import { useState } from 'react';
import { PlatformIcon } from '@/components/wireframe/ui';
import { samplePosts } from '@/lib/wireframe-data';

type ScheduledPost = { time?: string; platform: string; title: string };

const days = ['Mon 14', 'Tue 15', 'Wed 16', 'Thu 17', 'Fri 18', 'Sat 19', 'Sun 20'];

const optimalTimes = [
  { p: 'linkedin', name: 'LinkedIn', times: 'Tue-Thu, 9am-12pm' },
  { p: 'twitter', name: 'X / Twitter', times: 'Mon-Fri, 12pm-3pm' },
  { p: 'facebook', name: 'Facebook', times: 'Wed-Fri, 1pm-4pm' },
  { p: 'instagram', name: 'Instagram', times: 'Mon-Fri, 11am-2pm' },
];

export default function CalendarRoute() {
  const [view, setView] = useState<'week' | 'month'>('week');

  const scheduledPosts: Record<number, ScheduledPost[]> = {};
  samplePosts
    .filter((p) => p.status === 'scheduled')
    .forEach((p) => {
      if (p.day === undefined) return;
      (scheduledPosts[p.day] = scheduledPosts[p.day] || []).push({
        time: p.time,
        platform: p.platforms[0],
        title: p.title,
      });
    });

  return (
    <div className="p-8 space-y-5">
      <h1 className="text-2xl font-semibold text-stone-900">Calendar</h1>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button className="p-2 hover:bg-stone-100 rounded-lg">◀</button>
          <span className="text-lg font-semibold text-stone-900">April 2026</span>
          <button className="p-2 hover:bg-stone-100 rounded-lg">▶</button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setView('week')}
            className={
              view === 'week'
                ? 'px-4 py-2 bg-stone-100 text-stone-900 rounded-lg font-medium'
                : 'px-4 py-2 text-stone-500 hover:text-stone-700 rounded-lg'
            }
          >
            Week
          </button>
          <button
            onClick={() => setView('month')}
            className={
              view === 'month'
                ? 'px-4 py-2 bg-stone-100 text-stone-900 rounded-lg font-medium'
                : 'px-4 py-2 text-stone-500 hover:text-stone-700 rounded-lg'
            }
          >
            Month
          </button>
        </div>
      </div>

      <div className="border border-stone-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
          {days.map((day, i) => (
            <div
              key={day}
              className={`px-3 py-3 text-center text-sm ${
                i === 3 ? 'font-semibold text-violet-600 bg-violet-50' : 'text-stone-500'
              }`}
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 min-h-[320px]">
          {days.map((_, i) => (
            <div
              key={i}
              className={`p-2 border-r border-stone-100 last:border-r-0 ${
                i === 3 ? 'bg-violet-50/30 ring-2 ring-inset ring-violet-200' : ''
              }`}
            >
              {scheduledPosts[i]?.map((post, j) => (
                <div
                  key={j}
                  className={`mb-2 p-2 rounded-lg text-xs cursor-pointer ${
                    post.platform === 'linkedin'
                      ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      : post.platform === 'twitter'
                        ? 'bg-stone-100 text-stone-700'
                        : 'bg-indigo-50 text-indigo-700'
                  }`}
                >
                  {post.time && <div className="font-medium">{post.time}</div>}
                  <div className="truncate">{post.title}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-stone-50 rounded-xl p-5">
        <h3 className="font-semibold text-stone-900 mb-3">Optimal posting times</h3>
        <div className="grid gap-4 text-sm grid-cols-2 lg:grid-cols-4">
          {optimalTimes.map((item) => (
            <div key={item.p} className="flex items-center gap-3">
              <PlatformIcon platform={item.p} />
              <div>
                <div className="font-medium text-stone-700">{item.name}</div>
                <div className="text-stone-500">{item.times}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
